/**
 * Federal (Bund) admin page - shows Germany-wide data with tabs
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Globe, Database, Users } from 'lucide-react'
import { formatNumber } from '../../lib/utils'
import { EntityPageTabs, type EntityPageTab } from '../components/EntityPageTabs'
import { DataSourcesView } from '../components/DataSourcesView'
import IndicatorSection from '../../components/IndicatorSection'

interface FederalSnapshot {
  reference_year: number
  population: number | null
  area_km2: number | null
  population_density: number | null
  num_states: number | null
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8899'

async function fetchFederalAvailableEditions(): Promise<number[]> {
  const response = await fetch(`${API_BASE_URL}/api/federal/available-editions`)
  if (!response.ok) throw new Error('Failed to fetch available editions')
  return response.json()
}

async function fetchFederalSnapshot(edition: number): Promise<FederalSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/federal/snapshot/${edition}`)
  if (!response.ok) throw new Error('Failed to fetch federal snapshot')
  return response.json()
}

function DataCard({ title, icon: Icon, children }: { title: string, icon: React.ComponentType<{ className?: string }>, children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-gut8 border border-border-gut8-light p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-gut8-anthracite">{title}</h3>
      </div>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  )
}

function DataRow({ label, value, unit }: { label: string, value: React.ReactNode, unit?: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-border-gut8-light last:border-0">
      <span className="text-sm text-text-muted">{label}</span>
      <span className="text-sm font-medium text-gut8-anthracite">
        {value ?? '-'}
        {unit && value && <span className="ml-1 text-text-muted font-normal">{unit}</span>}
      </span>
    </div>
  )
}

export function FederalPage() {
  const [activeTab, setActiveTab] = useState<EntityPageTab>('table')

  const { data: availableEditions = [], isLoading: editionsLoading } = useQuery({
    queryKey: ['federal', 'available-editions'],
    queryFn: fetchFederalAvailableEditions,
  })

  const { data: latestSnapshot, isLoading: snapshotLoading } = useQuery({
    queryKey: ['federal', 'snapshot', availableEditions[0]],
    queryFn: () => fetchFederalSnapshot(availableEditions[0]),
    enabled: availableEditions.length > 0,
  })

  const isLoading = editionsLoading || snapshotLoading

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Globe className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gut8-anthracite">Deutschland</h1>
          <p className="text-sm text-text-muted">Bundesweite Daten</p>
        </div>
      </div>

      {/* Tabs */}
      <EntityPageTabs activeTab={activeTab} onTabChange={setActiveTab} tableTabLabel="Daten" />

      {/* Tab Content */}
      {activeTab === 'table' ? (
        <>
          {/* Data Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* GV100 Snapshot Data */}
            <DataCard title="Gemeindeverzeichnis" icon={Database}>
              <DataRow label="Referenzjahr" value={latestSnapshot?.reference_year} />
              <DataRow label="Einwohner" value={latestSnapshot?.population ? formatNumber(latestSnapshot.population) : null} />
              <DataRow label="Fläche" value={latestSnapshot?.area_km2 ? formatNumber(latestSnapshot.area_km2, { decimals: 2 }) : null} unit="km²" />
              <DataRow label="Bevölkerungsdichte" value={latestSnapshot?.population_density ? formatNumber(latestSnapshot.population_density, { decimals: 1 }) : null} unit="EW/km²" />
              <DataRow label="Bundesländer" value={latestSnapshot?.num_states} />
            </DataCard>

            {/* Available Editions */}
            <DataCard title="Verfügbare Editionen" icon={Users}>
              {availableEditions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableEditions.map((edition) => (
                    <span
                      key={edition}
                      className="px-3 py-1 bg-primary-subtle text-primary-dark rounded-full text-sm font-medium"
                    >
                      {edition}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted">Keine Editionen verfügbar</p>
              )}
            </DataCard>
          </div>

          {/* Indicators (Regionalstatistik, INKAR, etc.) */}
          <IndicatorSection
            entityType="federal"
            ags="DG"
          />
        </>
      ) : (
        <DataSourcesView entityLevel="federal" />
      )}
    </div>
  )
}
